import { useCallback, useEffect, useMemo, useState } from "react";

import {
  ChevronDownIcon,
  ChevronUpIcon,
  MapIcon,
} from "@heroicons/react/24/outline";

import { ILead, LeadStatus } from "../../models/Lead";
import { ICalendarTrip } from "../../models/Trip";
import { ITruck } from "../../models/Truck";
import {
  IApplyOptimiseRequest,
  IOptimiseResult,
} from "../../models/TripOptimisation";
import useTrips from "../../data/useTrips";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import { getLeadLocation } from "../../utils/getLeadLocation";
import PriceFormat from "../../utils/priceFormat";
import CollapsibleSection from "../shared/CollapsibleSection";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import TripDayMap, { RouteGeometry, TripMapMarker } from "./TripDayMap";
import OptimiseTripsModal from "./OptimiseTripsModal";
import PrintOptimisedRoute from "./PrintOptimisedRoute";

const SHOW_MAP_STORAGE_KEY = "tripManager.showMap";

// The yard (DT3 4HG) the routes run out-and-back from — the same site as Adam's
// SITE_DEPOT, using its exact coordinates. The API routes from the same place
// (it geocodes the postcode, falling back to these values — see
// TripOptimisationConstants), so keep the two in sync.
// TODO: pull this from a Yard-type Location record (Location.Point) via the API
// so the pin and the routing depot both follow the real yard.
const SITE_DEPOT = { lat: 50.668463, lng: -2.562743 };

// Distinct colours cycled per trip so each truck's route is easy to tell apart.
// Shared with the trip grid (TripManager) so pins, routes and lead cards agree.
export const TRIP_COLOURS = [
  "#2563eb", "#dc2626", "#16a34a", "#d97706", "#7c3aed",
  "#0891b2", "#db2777", "#65a30d", "#4f46e5", "#ea580c",
];

interface CollectionMapSectionProps {
  selectedDay?: string;
  selectedTrip?: string;
  /** The day's leads (used to plot the current markers). */
  leads: ILead[];
  /** The day's trips (for colours, truck names and driver assignment). */
  trips: ICalendarTrip[];
  trucksById: Map<string, ITruck>;
  selectedLead?: ILead;
  onSelectLead: (lead: ILead) => void;
  year: number;
  week: number;
}

/**
 * Collapsible "Collection map" for the Trip Manager. Shows the day's collections
 * on a Leaflet map and lets the office optimise a trip (or the whole day) via the
 * backend — the API does the Mapbox routing and drivers'-hours maths and returns a
 * proposed order + schedule, which is previewed here and applied on confirm.
 */
export default function CollectionMapSection({
  selectedDay,
  selectedTrip,
  leads,
  trips,
  trucksById,
  selectedLead,
  onSelectLead,
  year,
  week,
}: CollectionMapSectionProps) {
  const { optimise, applyOptimise, geocodeLead } = useTrips(year, week);
  const { saveHandlers } = useDefaultCRUDHandlers("Trip");

  const [showMap, setShowMap] = useState<boolean>(() => {
    if (typeof window === "undefined") return true;
    return window.localStorage.getItem(SHOW_MAP_STORAGE_KEY) !== "false";
  });
  const [result, setResult] = useState<IOptimiseResult>();
  // The last optimised plan kept purely for printing. Unlike `result` it isn't
  // cleared when a proposal is applied, so "Print schedule" stays available for
  // the trip you just re-ordered. Cleared on discard/undo or a selection change.
  const [printableResult, setPrintableResult] = useState<IOptimiseResult>();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [allTripsMode, setAllTripsMode] = useState(false);
  const [startTimes, setStartTimes] = useState<Record<string, string>>({});
  // The pre-apply state returned by the backend, replayed through apply to undo.
  const [undoState, setUndoState] = useState<IApplyOptimiseRequest>();
  const [isUndoing, setIsUndoing] = useState(false);
  const [lastOptimisedAt, setLastOptimisedAt] = useState<string>();
  const [geocodingLeadId, setGeocodingLeadId] = useState<string>();

  useEffect(() => {
    window.localStorage.setItem(SHOW_MAP_STORAGE_KEY, showMap.toString());
  }, [showMap]);

  // Clear any proposed route (and undo snapshot — it's only valid for the day it
  // was applied on) when the day or trip selection changes.
  useEffect(() => {
    setResult(undefined);
    setPrintableResult(undefined);
    setPreviewOpen(false);
    setModalOpen(false);
    setUndoState(undefined);
    setLastOptimisedAt(undefined);
  }, [selectedDay, selectedTrip]);

  const depot = SITE_DEPOT;

  // Stable colour per trip for the day.
  const tripColours = useMemo(() => {
    const map: Record<string, string> = {};
    trips.forEach((trip, index) => {
      map[trip.id] = TRIP_COLOURS[index % TRIP_COLOURS.length];
    });
    return map;
  }, [trips]);

  const tripsById = useMemo(() => new Map(trips.map((t) => [t.id, t])), [trips]);

  // A lead's map pin. Leads without a trip are flagged so the map can style them
  // as unscheduled (no visiting order to show).
  const toMarker = useCallback(
    (lead: ILead): TripMapMarker | null => {
      const location = getLeadLocation(lead);
      const lat = location?.latitude;
      const lng = location?.longitude;
      if (!lat || !lng) return null;

      const trip = lead.tripId ? tripsById.get(lead.tripId) : undefined;
      const truck = trip ? trucksById.get(trip.truckId) : undefined;

      return {
        id: lead.id,
        lat,
        lng,
        order: (lead.order ?? 0) + 1,
        colour: (lead.tripId && tripColours[lead.tripId]) || "#6b7280",
        isVan: lead.vehicle?.isVan ?? false,
        is4x4: lead.vehicle?.driveType === "4x4",
        truckName: truck?.name ?? "",
        label: lead.vehicle?.vrm ?? "No VRM",
        postcode: location?.postcode ?? "",
        tripId: lead.tripId ?? "",
        isUnscheduled: !lead.tripId,
      };
    },
    [tripsById, trucksById, tripColours]
  );

  // Current markers: routable day leads (never cancelled ones), optionally
  // narrowed to the selected trip.
  const markers = useMemo<TripMapMarker[]>(
    () =>
      leads
        .filter((lead) => lead.status !== LeadStatus["Cancelled"])
        .filter((lead) => !!lead.tripId)
        .filter((lead) => !selectedTrip || lead.tripId === selectedTrip)
        .map(toMarker)
        .filter((m): m is TripMapMarker => m !== null),
    [leads, selectedTrip, toMarker]
  );

  // The selected lead always gets a pin, even when it isn't one of the day's
  // routed stops - an unscheduled lead picked from the panel, or a lead sitting
  // outside the selected trip.
  const selectedMarkers = useMemo<TripMapMarker[]>(() => {
    if (!selectedLead) return [];
    if (markers.some((m) => m.id === selectedLead.id)) return [];

    const marker = toMarker(selectedLead);
    return marker ? [marker] : [];
  }, [selectedLead, markers, toMarker]);

  // Proposed markers + road geometry, built from the backend result while previewing.
  const previewMarkers = useMemo<TripMapMarker[] | undefined>(() => {
    if (!previewOpen || !result) return undefined;
    return result.trips.flatMap((trip) =>
      trip.stops.map((stop) => ({
        id: stop.leadId,
        lat: stop.latitude,
        lng: stop.longitude,
        order: stop.order + 1,
        colour: tripColours[trip.tripId] ?? "#6b7280",
        isVan: stop.isVan,
        is4x4: stop.is4x4,
        truckName: trip.truckName ?? "",
        label: stop.vrm ?? "No VRM",
        postcode: stop.postcode ?? "",
        tripId: trip.tripId,
      }))
    );
  }, [previewOpen, result, tripColours]);

  const previewGeometry = useMemo<RouteGeometry | undefined>(() => {
    if (!previewOpen || !result) return undefined;
    const geometry: RouteGeometry = {};
    result.trips.forEach((trip) => {
      if (trip.geometry?.length) geometry[trip.tripId] = trip.geometry;
    });
    return geometry;
  }, [previewOpen, result]);

  const runOptimise = (allTrips: boolean, startTimeOverrides?: Record<string, string>) => {
    if (!selectedDay) return;

    setAllTripsMode(allTrips);
    optimise.mutate(
      {
        date: selectedDay,
        tripId: allTrips ? undefined : selectedTrip,
        allTrips,
        startTimes: startTimeOverrides ?? startTimes,
      },
      {
        onSuccess: (res: any) => {
          const data: IOptimiseResult = res.data;
          setResult(data);
          setPrintableResult(data);
          // Note: startTimes only holds times the user explicitly picked. Backend
          // resolved/chained times are shown via trip.startTime fallbacks instead —
          // echoing them back would freeze a 2nd trip's chained departure.
          setLastOptimisedAt(
            new Date().toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })
          );
          setPreviewOpen(true);
          setShowMap(true);
          setModalOpen(true);
        },
        onError: () => saveHandlers.onError(),
      }
    );
  };

  // Changing a trip's start time re-runs the preview so break/arrival times refresh.
  const handleStartTimeChange = (tripId: string, value: string) => {
    const next = { ...startTimes, [tripId]: value };
    setStartTimes(next);
    optimise.mutate(
      {
        date: selectedDay!,
        tripId: allTripsMode ? undefined : selectedTrip,
        allTrips: allTripsMode,
        startTimes: next,
      },
      {
        onSuccess: (res: any) => {
          setResult(res.data);
          setPrintableResult(res.data);
        },
        onError: () => saveHandlers.onError(),
      }
    );
  };

  const handleApply = () => {
    if (!result || !selectedDay) return;

    applyOptimise.mutate(
      {
        date: selectedDay,
        trips: result.trips.map((trip) => ({
          tripId: trip.tripId,
          startTime: startTimes[trip.tripId] ?? trip.startTime,
          leadIdsInOrder: trip.stops.map((stop) => stop.leadId),
        })),
      },
      {
        onSuccess: (res) => {
          saveHandlers.onSuccess();
          // Keep the backend's pre-apply snapshot so the change can be undone.
          setUndoState(res.data?.previous);
          setModalOpen(false);
          setPreviewOpen(false);
          setResult(undefined);
        },
        onError: () => saveHandlers.onError(),
      }
    );
  };

  // Revert an applied plan by replaying the pre-apply snapshot through apply.
  const handleUndo = () => {
    if (!undoState) return;

    setIsUndoing(true);
    applyOptimise.mutate(undoState, {
      onSuccess: () => {
        saveHandlers.onSuccess();
        setUndoState(undefined);
        setResult(undefined);
        setPrintableResult(undefined);
        setPreviewOpen(false);
      },
      onError: () => saveHandlers.onError(),
      onSettled: () => setIsUndoing(false),
    });
  };

  // Geocode an unroutable collection's postcode, then refresh the preview so it
  // joins the route.
  const handleGeocode = (leadId: string) => {
    setGeocodingLeadId(leadId);
    geocodeLead.mutate(leadId, {
      onSuccess: () => runOptimise(allTripsMode),
      onError: () => saveHandlers.onError(),
      onSettled: () => setGeocodingLeadId(undefined),
    });
  };

  const handleDiscard = () => {
    setResult(undefined);
    setPrintableResult(undefined);
    setPreviewOpen(false);
    setModalOpen(false);
  };

  const handleMarkerClick = (id: string) => {
    // The selected lead may not be in the day's leads (e.g. still unscheduled),
    // so fall back to it directly - clicking its pin should still deselect it.
    const lead =
      leads.find((l) => l.id === id) ??
      (selectedLead?.id === id ? selectedLead : undefined);
    if (lead) onSelectLead(lead);
  };

  // VRM/postcode details for the unroutable leads listed in the review modal.
  const unroutableLeads = useMemo(
    () =>
      (result?.unroutableLeadIds ?? []).map((id) => {
        const lead = leads.find((l) => l.id === id);
        return {
          leadId: id,
          vrm: lead?.vehicle?.vrm ?? "No VRM",
          makeModel: [lead?.vehicle?.combinedMake, lead?.vehicle?.combinedModel]
            .filter(Boolean)
            .join(" "),
          postcode: (lead && getLeadLocation(lead)?.postcode) ?? "",
        };
      }),
    [result, leads]
  );

  const savings = result?.savings;

  return (
    <div className="mt-3 rounded-md border border-gray-300 bg-white">
      <div className="flex items-center justify-between px-3 py-2">
        <button
          onClick={() => setShowMap((v) => !v)}
          className="flex items-center gap-x-2 text-sm font-semibold text-gray-700"
        >
          <MapIcon className="h-5 w-5 text-gray-500" />
          Collection map
          {showMap ? (
            <ChevronUpIcon className="h-4 w-4 text-gray-400" />
          ) : (
            <ChevronDownIcon className="h-4 w-4 text-gray-400" />
          )}
        </button>

        <div className="flex items-center gap-x-2">
          {lastOptimisedAt && (
            <span className="text-xs text-gray-400">Last optimised {lastOptimisedAt}</span>
          )}
          {undoState && (
            <ButtonInput
              label="Undo"
              isSubmit={false}
              classes=""
              size="sm"
              onClick={handleUndo}
              isLoading={isUndoing}
            />
          )}
          {selectedTrip && (
            <ButtonInput
              label="Optimise trip"
              isSubmit={false}
              classes=""
              size="sm"
              onClick={() => runOptimise(false)}
              isLoading={optimise.isLoading && !allTripsMode}
            />
          )}
          {/* Prints the selected trip's optimised schedule. Disabled until it's
              been optimised — `result` is cleared whenever the trip changes, so
              it only ever holds the current selection's plan. */}
          {selectedTrip && (
            <PrintOptimisedRoute
              result={printableResult}
              selectedDay={selectedDay}
              tripColours={tripColours}
              instanceId="trip-schedule"
              buttonLabel="Print schedule"
              buttonSize="sm"
            />
          )}
          <ButtonInput
            label="Optimise all trips"
            isSubmit={false}
            classes=""
            size="sm"
            onClick={() => runOptimise(true)}
            isLoading={optimise.isLoading && allTripsMode}
          />
        </div>
      </div>

      {previewOpen && result && savings && (
        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-200 bg-primary-50 px-3 py-2 text-sm">
          <span className="text-gray-700">
            Proposed route ·{" "}
            {savings.savingsKm >= 0
              ? `saves ${savings.savingsKm.toFixed(1)} km (${PriceFormat(savings.fuelCost)})`
              : `+${Math.abs(savings.savingsKm).toFixed(1)} km`}
          </span>
          <div className="flex items-center gap-x-2">
            <PrintOptimisedRoute
              result={result}
              selectedDay={selectedDay}
              tripColours={tripColours}
              instanceId="banner"
              buttonSize="sm"
            />
            <ButtonInput isSubmit={false} classes="" size="sm" onClick={() => setModalOpen(true)}>
              <span>Details</span>
            </ButtonInput>
            <ButtonInput isSubmit={false} classes="" size="sm" onClick={handleDiscard}>
              <span>Discard</span>
            </ButtonInput>
            <ButtonInput
              label="Apply order"
              isSubmit={false}
              classes=""
              size="sm"
              onClick={handleApply}
              isLoading={applyOptimise.isLoading}
            />
          </div>
        </div>
      )}

      <CollapsibleSection isOpen={showMap} className="px-3 pb-3">
        {showMap && (
          <TripDayMap
            key={selectedDay}
            markers={markers}
            previewMarkers={previewMarkers}
            previewGeometry={previewGeometry}
            depot={depot}
            extraMarkers={selectedMarkers}
            selectedLeadId={selectedLead?.id}
            onMarkerClick={handleMarkerClick}
          />
        )}
      </CollapsibleSection>

      <OptimiseTripsModal
        open={modalOpen}
        setOpen={setModalOpen}
        result={result}
        tripColours={tripColours}
        startTimes={startTimes}
        onStartTimeChange={handleStartTimeChange}
        onConfirm={handleApply}
        isSaving={applyOptimise.isLoading}
        isOptimising={optimise.isLoading}
        unroutableLeads={unroutableLeads}
        onGeocode={handleGeocode}
        geocodingLeadId={geocodingLeadId}
        selectedDay={selectedDay}
      />
    </div>
  );
}
